import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Upload,
  Database,
  X,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  Search,
  Check,
  ArrowLeft,
  Table,
  AlertCircle,
  ExternalLink,
  Trash2,
  RefreshCw,
  Library,
  Lock,
  Clock3,
  Settings,
  Unplug,
  SlidersHorizontal,
  ChevronDown,
  Menu,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { bigqueryApi } from '../../api/bigqueryApi';
import { datasetApi } from '../../api/datasetApi';
import { showErrorToast, showInfoToast, showSuccessToast } from '../../lib/toast';
import { useAuth } from '../../context/AuthContext';
import { FEATURE_INTEGRATION_BIGQUERY } from '../../constants/capabilities';
import { MAX_DATASETS_PER_WORKSPACE } from '../../constants/limits';
import {
  ALLOWED_DATASET_UPLOAD_ACCEPT,
  MAX_DATASET_UPLOAD_BYTES,
  formatDatasetUploadMaxSizeLabel,
  validateDatasetUploadFilename,
} from '../../constants/datasetUploadLimits';
import { spatialApi } from '../../api/spatialApi';
import CatalogSampleThumbnail from './CatalogSampleThumbnail';
import { CatalogDatasetMeta, CatalogSampleLicenseDisclaimer } from './CatalogLicenseNotice';
import { isUploadDatasetType } from '../../utils/datasetDisplayUtils';
import {
  DatasetInventoryTree,
  InventoryTreeNode,
  buildSortedDatasetTree,
  collectDatasetTreeNodeIds,
  datasetLeafIcon,
  datasetLeafItem,
  hasManteoDatasets,
} from './inventoryTreeShared';

function createEmptyCatalogSampleFilters() {
  return {
    categories: [],
    types: [],
    tags: [],
    licenses: [],
    dataSources: [],
    spatial: [],
    temporal: [],
  };
}

function CatalogFacetCheckboxGroup({ label, options, selected, onToggle, onClearSection }) {
  const [expanded, setExpanded] = useState(false);

  if (!options.length) return null;

  return (
    <div className="mb-2.5 last:mb-0">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30 rounded-md"
          aria-expanded={expanded}
        >
          <ChevronDown
            size={13}
            className={cn(
              'shrink-0 text-slate-400 transition-transform duration-200 dark:text-gray-500',
              expanded && 'rotate-180'
            )}
            aria-hidden
          />
          <span className="truncate text-xs font-medium text-slate-700 dark:text-gray-200">
            {label}
          </span>
          {selected.length > 0 ? (
            <span className="shrink-0 rounded-full bg-emerald-600/12 px-1.5 py-px text-[10px] font-medium tabular-nums text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300">
              {selected.length}
            </span>
          ) : null}
        </button>
        {selected.length > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClearSection();
            }}
            className="shrink-0 px-1 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            Clear
          </button>
        ) : null}
      </div>
      {expanded ? (
        <ul className="mt-1 space-y-0.5 pl-5 max-h-32 overflow-y-auto">
          {options.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <li key={opt}>
                <label className="flex cursor-pointer items-start gap-2 rounded-md py-0.5 group hover:bg-slate-50/80 dark:hover:bg-gray-800/50">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500/40 dark:border-gray-600 dark:bg-gray-900"
                    checked={checked}
                    onChange={() => onToggle(opt)}
                  />
                  <span className="text-[11px] leading-snug text-slate-600 dark:text-gray-400 group-hover:text-slate-900 dark:group-hover:text-gray-200">
                    {opt}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

const SpatialSelector = ({ columnName, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(async () => {
      try {
        const res = await spatialApi.searchProjections(query);
        setResults(res);
      } catch {
        setResults([]);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query, isOpen]);

  return (
    <div
      ref={selectorRef}
      className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-gray-900/40"
    >
      <div
        className={cn('flex items-start justify-between gap-3', isOpen && 'cursor-pointer')}
        onClick={() => isOpen && setIsOpen(false)}
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="break-words text-xs font-medium leading-snug text-gray-900 dark:text-gray-100">
            {columnName}
          </div>
          <div className="mt-1.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
            <span className="text-gray-500 dark:text-gray-500">Coordinate system</span>
            <span className="mx-1 text-gray-400">·</span>
            <span className="break-words">
              {value?.name || 'Unknown'} (EPSG:{value?.source_srid ?? value?.srid ?? '?'})
            </span>
          </div>
        </div>
        {!isOpen ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
            className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-900"
          >
            Change
          </button>
        ) : (
          <button type="button" className="shrink-0 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Close">
            <X size={16} />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="mt-3 space-y-3 border-t border-gray-200 pt-3 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-xs text-gray-900 outline-none ring-0 placeholder:text-gray-400 focus:border-gray-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:focus:border-gray-500"
              placeholder="Search by name or EPSG…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="max-h-48 overflow-y-auto space-y-0.5 custom-scrollbar rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-950">
            {results.map((r) => (
              <button
                key={r.srid}
                type="button"
                onClick={() => {
                  onChange({ ...value, source_srid: r.srid, name: r.name });
                  setIsOpen(false);
                  setQuery('');
                }}
                className="flex w-full items-start justify-between gap-2 rounded-md px-2 py-2 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-900"
              >
                <span className="min-w-0 flex-1 break-words text-gray-900 dark:text-gray-100">{r.name}</span>
                <span className="shrink-0 tabular-nums text-[11px] text-gray-500 dark:text-gray-400">{r.srid}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-full py-1.5 text-[11px] text-gray-500 hover:text-gray-800 dark:hover:text-gray-300"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

const ConnectModal = ({
  isOpen,
  mode = 'table',
  onClose,
  onConnect,
  onBillingRequired,
  onConnectionUpdated,
  initialStep,
  currentSources = []
}) => {
  const { capabilities, user } = useAuth();
  const userFolderLabel = user?.email || 'My data';
  const bigqueryEnabled = capabilities?.features?.[FEATURE_INTEGRATION_BIGQUERY] ?? true;
  const [step, setStep] = useState('select'); // 'select' | 'upload' | 'samples' | 'bigquery-auth' | 'bigquery-dataset' | 'bigquery-table' | 'bigquery-confirm' | 'existing'
  const [activeSourceTab, setActiveSourceTab] = useState('upload');
  const [searchQuery, setSearchQuery] = useState('');
  const [spatialReview, setSpatialReview] = useState({}); // column_name -> { srid, name }
  const [pendingFinalize, setPendingFinalize] = useState(null);
  const [layerOptions, setLayerOptions] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState('');
  const [uploadGate, setUploadGate] = useState(null); // 'layer' | 'crs'
  const [selection, setSelection] = useState({
    project: null,
    dataset: null,
    tables: []
  });
  const [isUploading, setIsUploading] = useState(false);
  const [bqStatus, setBqStatus] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [tables, setTables] = useState([]);
  const [allUserDatasets, setAllUserDatasets] = useState([]);
  const [sampleDatasets, setSampleDatasets] = useState([]);
  const [catalogSampleFilters, setCatalogSampleFilters] = useState(createEmptyCatalogSampleFilters);
  const [catalogFiltersOpen, setCatalogFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('idle'); // 'idle' | 'preparing' | 'uploading' | 'verifying' | 'ready'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [expandedLibraryNodes, setExpandedLibraryNodes] = useState(new Set());
  const libraryTreeExpandedOnceRef = useRef(false);
  const currentWorkspaceDatasetCount = currentSources.length;

  const checkBQStatus = useCallback(async () => {
    if (!isOpen) return;

    try {
      const status = await bigqueryApi.getStatus();
      setBqStatus(status);
      // Removed dependency on 'step' here to avoid reload loops
      // The logic to load projects if connected is moved elsewhere or handled differently
    } catch (err) {
      if (err.code !== 'ECONNABORTED') {
        console.error('Failed to check BQ status:', err);
      }
    }
  }, [isOpen]); // Added isOpen to deps


  const loadDatasets = useCallback(async (projectId) => {
    setIsLoading(true);
    try {
      const data = await bigqueryApi.getDatasets(projectId);
      setDatasets(data);
      setStep('bigquery-dataset');
    } catch (err) {
      console.error('Failed to load datasets:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadTables = useCallback(async (projectId, datasetId) => {
    setIsLoading(true);
    try {
      const data = await bigqueryApi.getTables(projectId, datasetId);
      setTables(data);
      setStep('bigquery-table');
    } catch (err) {
      console.error('Failed to load tables:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadAllUserDatasets = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const data = await datasetApi.getDatasets();
      setAllUserDatasets(data);
    } catch (err) {
      console.error('Failed to load user datasets:', err);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  const loadSampleDatasets = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await datasetApi.getSamples();
      setSampleDatasets(data);
    } catch (err) {
      console.error('Failed to load sample datasets:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load sample datasets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const toggleCatalogFacet = useCallback((facetKey, value) => {
    setCatalogSampleFilters((prev) => {
      const cur = prev[facetKey];
      const nextList = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
      return { ...prev, [facetKey]: nextList };
    });
  }, []);

  const clearCatalogFacet = useCallback((facetKey) => {
    setCatalogSampleFilters((prev) => ({ ...prev, [facetKey]: [] }));
  }, []);

  const clearAllCatalogSampleFilters = useCallback(() => {
    setCatalogSampleFilters(createEmptyCatalogSampleFilters());
  }, []);

  const { catalogFacetOptions, filteredCatalogSamples } = useMemo(() => {
    const categories = new Set();
    const types = new Set();
    const tags = new Set();
    const licenses = new Set();
    const dataSources = new Set();
    const spatial = new Set();
    const temporal = new Set();
    for (const s of sampleDatasets) {
      if (s.category) categories.add(s.category);
      if (s.license) licenses.add(s.license);
      if (s.data_source) dataSources.add(s.data_source);
      if (s.spatial_coverage) spatial.add(s.spatial_coverage);
      if (s.temporal_coverage) temporal.add(s.temporal_coverage);
      for (const t of s.tags || []) tags.add(t);
      for (const t of s.types || []) types.add(t);
    }
    const facetSort = (a, b) => a.localeCompare(b);
    const options = {
      categories: [...categories].sort(facetSort),
      types: [...types].sort(facetSort),
      tags: [...tags].sort(facetSort),
      licenses: [...licenses].sort(facetSort),
      dataSources: [...dataSources].sort(facetSort),
      spatial: [...spatial].sort(facetSort),
      temporal: [...temporal].sort(facetSort),
    };

    const filtered = sampleDatasets.filter((s) => {
      if (catalogSampleFilters.categories.length && !catalogSampleFilters.categories.includes(s.category)) {
        return false;
      }
      if (catalogSampleFilters.types.length) {
        const st = s.types || [];
        if (!catalogSampleFilters.types.some((t) => st.includes(t))) return false;
      }
      if (catalogSampleFilters.licenses.length && !catalogSampleFilters.licenses.includes(s.license)) {
        return false;
      }
      if (catalogSampleFilters.dataSources.length && !catalogSampleFilters.dataSources.includes(s.data_source)) {
        return false;
      }
      if (catalogSampleFilters.spatial.length && !catalogSampleFilters.spatial.includes(s.spatial_coverage)) {
        return false;
      }
      if (catalogSampleFilters.temporal.length && !catalogSampleFilters.temporal.includes(s.temporal_coverage)) {
        return false;
      }
      if (catalogSampleFilters.tags.length) {
        const st = s.tags || [];
        if (!catalogSampleFilters.tags.some((t) => st.includes(t))) return false;
      }
      const q = searchQuery.toLowerCase().trim();
      if (q) {
        const parts = [
          s.name,
          s.description,
          s.category,
          s.license,
          s.url,
          s.data_source,
          s.spatial_coverage,
          s.temporal_coverage,
          s.thumbnail_url,
          ...(s.tags || []),
          ...(s.types || []),
        ];
        const hay = parts.filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return { catalogFacetOptions: options, filteredCatalogSamples: filtered };
  }, [sampleDatasets, catalogSampleFilters, searchQuery]);

  const activeCatalogFilterCount = useMemo(
    () => Object.values(catalogSampleFilters).reduce((sum, values) => sum + values.length, 0),
    [catalogSampleFilters]
  );

  const handleBQLanding = useCallback(async () => {
    if (mode === 'table') {
      setActiveSourceTab('connections');
    }
    if (!bigqueryEnabled) {
      setError('This connector is not included in your current plan.');
      return;
    }
    if (mode === 'connection' && bqStatus?.connected) {
      showInfoToast('You can only have one BigQuery connection. Use connection settings to manage it.');
      return;
    }
    if (bqStatus?.connected) {
      if (!bqStatus.billing_project_id) {
        // Connected but no billing project - trigger the billing modal first
        if (onBillingRequired) {
          onBillingRequired();
        } else {
          setStep('bigquery-auth');
        }
      } else {
        // Connected and has billing project - proceed to dataset selection
        setSelection(prev => ({
          ...prev,
          project: { project_id: bqStatus.billing_project_id, friendly_name: bqStatus.billing_project_id }
        }));
        loadDatasets(bqStatus.billing_project_id);
      }
    } else {
      setStep('bigquery-auth');
    }
  }, [bigqueryEnabled, bqStatus, loadDatasets, mode, onBillingRequired]);

  const handleDisconnectBigQuery = useCallback(async (event) => {
    event?.stopPropagation?.();
    if (!bqStatus?.connected) return;
    const confirmed = window.confirm('Unlink BigQuery connection?');
    if (!confirmed) return;

    try {
      await bigqueryApi.disconnect();
      showSuccessToast('BigQuery connection unlinked.');
      await checkBQStatus();
      if (onConnectionUpdated) {
        await onConnectionUpdated();
      }
    } catch (err) {
      console.error('Failed to disconnect BigQuery:', err);
      showErrorToast('Could not unlink BigQuery connection. Please try again.');
    }
  }, [bqStatus?.connected, checkBQStatus, onConnectionUpdated]);

  React.useEffect(() => {
    if (isOpen) {
      checkBQStatus();
      if (mode === 'workspace' || step === 'existing') {
        loadAllUserDatasets();
      }
    }
  }, [isOpen, checkBQStatus, mode, step, loadAllUserDatasets]);

  // Handle auto-project load if we just authorized or resumed
  useEffect(() => {
    if (isOpen && bqStatus?.connected && (step === 'bigquery-auth' || ((step === 'select' || step === 'connections') && initialStep === 'bigquery-dataset'))) {
      handleBQLanding();
    }
  }, [isOpen, bqStatus, step, initialStep, handleBQLanding]);



  // Polling for library updates when datasets are loading/generating
  useEffect(() => {
    if (!isOpen) return;

    const hasPending = allUserDatasets.some(d => d.status === 'loading' || d.status === 'generating');
    if (!hasPending) return;

    const interval = setInterval(() => {
      loadAllUserDatasets(true); // Silent update
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, allUserDatasets, loadAllUserDatasets]);

  const handleBack = () => {
    const homeStep = mode === 'table' ? 'connections' : 'select';
    if (mode === 'table') {
      setActiveSourceTab('connections');
    }
    if (step === 'upload' || step === 'samples' || step === 'bigquery-auth' || step === 'existing') setStep(homeStep);
    else if (step === 'bigquery-dataset') setStep(homeStep);
    else if (step === 'bigquery-table') setStep('bigquery-dataset');
    else if (step === 'bigquery-confirm') setStep('bigquery-table');
    setError(null);
  };

  const handleBQAuth = async () => {
    try {
      const { url } = await bigqueryApi.getLoginUrl();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to get login URL:', err);
    }
  };

  const handleDatasetSelect = (dataset) => {
    setSelection(prev => ({ ...prev, dataset }));
    loadTables(selection.project.project_id, dataset.dataset_id);
  };

  const fileInputRef = React.useRef(null);
  const verificationProgressTimerRef = React.useRef(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const stopVerificationProgress = useCallback(() => {
    if (verificationProgressTimerRef.current) {
      clearInterval(verificationProgressTimerRef.current);
      verificationProgressTimerRef.current = null;
    }
  }, []);

  const startVerificationProgress = useCallback(() => {
    stopVerificationProgress();
    setUploadProgress(80);
    verificationProgressTimerRef.current = setInterval(() => {
      setUploadProgress(prev => Math.min(prev + Math.floor(Math.random() * 3) + 1, 96));
    }, 900);
  }, [stopVerificationProgress]);

  const resetUploadState = useCallback(() => {
    stopVerificationProgress();
    setSpatialReview({});
    setPendingFinalize(null);
    setLayerOptions([]);
    setSelectedLayer('');
    setUploadGate(null);
    setUploadPhase('idle');
    setUploadProgress(0);
    setUploadFileName('');
  }, [stopVerificationProgress]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await performUpload(file);
  };

  const performUpload = async (file) => {
    if (mode === 'workspace' && currentWorkspaceDatasetCount >= MAX_DATASETS_PER_WORKSPACE) {
      setError(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
      return;
    }
    const suffixError = validateDatasetUploadFilename(file?.name);
    if (suffixError) {
      setError(suffixError);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    setIsUploading(true);
    setSpatialReview({});
    setPendingFinalize(null);
    setLayerOptions([]);
    setSelectedLayer('');
    setUploadGate(null);
    setUploadFileName(file.name);
    setUploadPhase('preparing');
    setUploadProgress(0);
    setError(null);
    try {
      if (typeof file.size === 'number' && file.size > MAX_DATASET_UPLOAD_BYTES) {
        setError(`File exceeds maximum size (${formatDatasetUploadMaxSizeLabel()}).`);
        setUploadPhase('idle');
        setUploadProgress(0);
        return;
      }
      // 1. Get signed URL
      const { upload_url, storage_path, max_upload_bytes } = await datasetApi.getUploadUrl(
        file.name,
        file.type
      );
      const serverLimit =
        typeof max_upload_bytes === 'number' ? max_upload_bytes : MAX_DATASET_UPLOAD_BYTES;
      if (typeof file.size === 'number' && file.size > serverLimit) {
        setError(
          `File exceeds maximum size (${formatDatasetUploadMaxSizeLabel(serverLimit)}).`
        );
        setUploadPhase('idle');
        setUploadProgress(0);
        return;
      }

      // 2. Upload to storage
      setUploadPhase('uploading');
      await datasetApi.uploadToSignedUrl(upload_url, file, (progress) => {
        setUploadProgress(progress);
      }, { maxUploadBytes: serverLimit });

      // 3. Finalize (may require layer / CRS selection via 422)
      setUploadPhase('verifying');
      startVerificationProgress();
      await runFinalizeUpload(storage_path, file.name, {});
      stopVerificationProgress();
      setUploadProgress(100);
    } catch (err) {
      console.error('Upload failed:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Upload failed. Please try again.';
      setError(errorMessage);
      stopVerificationProgress();
      setUploadPhase('idle');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone itself
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (
      isUploading ||
      isImporting ||
      (uploadPhase === 'ready' && (uploadGate === 'layer' || uploadGate === 'crs'))
    )
      return;

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    await performUpload(files[0]);
  };

  const toggleTableSelection = (table) => {
    setSelection(prev => {
      const exists = prev.tables.find(t => t.id === table.id);
      if (exists) {
        return { ...prev, tables: prev.tables.filter(t => t.id !== table.id) };
      }
      return {
        ...prev,
        tables: [...prev.tables, { ...table, contextProject: selection.project?.project_id, contextDataset: selection.dataset?.dataset_id }]
      };
    });
  };

  const handleFinishBQ = async () => {
    if (
      mode === 'workspace' &&
      currentWorkspaceDatasetCount + selection.tables.length > MAX_DATASETS_PER_WORKSPACE
    ) {
      setError(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
      return;
    }
    setIsImporting(true);
    try {
      // Transform selected tables to the format workspace expects
      const formattedData = {
        project: selection.project,
        dataset: selection.dataset,
        tables: selection.tables.map(t => ({
          id: t.full_id || t.id,
          name: t.table_id || t.name,
          size: 'N/A', // BQ API could provide this but let's keep it simple
          rows: 'N/A'
        }))
      };
      await onConnect('bigquery', formattedData);
      onClose();
      resetModal();
    } catch (err) {
      console.error('Import failed:', err);
    } finally {
      setIsImporting(false);
    }
  };

  const handleUploadInspectError = (err, { storagePath, filename, layerName, sourceSrid }) => {
    const data = err.response?.data || {};
    const code = data.code || data.error;
    if (code === 'LAYER_REQUIRED') {
      const layers = data.layers || [];
      setPendingFinalize({ storagePath, filename, layerName, sourceSrid });
      setLayerOptions(layers);
      setSelectedLayer(layers[0]?.name || '');
      setUploadGate('layer');
      setUploadPhase('ready');
      return true;
    }
    if (code === 'CRS_REQUIRED') {
      setPendingFinalize({ storagePath, filename, layerName, sourceSrid });
      setSpatialReview({
        dataset: {
          source_srid: 4326,
          srid: 4326,
          name: 'WGS 84 (Global)',
        },
      });
      setUploadGate('crs');
      setUploadPhase('ready');
      return true;
    }
    return false;
  };

  const runFinalizeUpload = async (storagePath, filename, { layerName, sourceSrid }) => {
    try {
      const spatialPlan = await datasetApi.inspectUpload(storagePath, filename, {
        layerName,
        sourceSrid,
      });
      const result = await datasetApi.finalizeUpload(storagePath, filename, null, {
        layerName,
        sourceSrid,
        spatialPlan,
      });
      await onConnect('upload', result);
      onClose();
      resetModal();
    } catch (err) {
      if (handleUploadInspectError(err, { storagePath, filename, layerName, sourceSrid })) {
        return;
      }
      throw err;
    }
  };

  const handleContinueUploadReview = async () => {
    if (!pendingFinalize) return;
    setIsImporting(true);
    setUploadPhase('verifying');
    startVerificationProgress();
    try {
      const layerName =
        uploadGate === 'layer' ? selectedLayer : pendingFinalize.layerName;
      const sourceSrid =
        uploadGate === 'crs'
          ? spatialReview.dataset?.source_srid
          : pendingFinalize.sourceSrid;
      await runFinalizeUpload(pendingFinalize.storagePath, pendingFinalize.filename, {
        layerName,
        sourceSrid,
      });
      stopVerificationProgress();
      setUploadProgress(100);
    } catch (err) {
      console.error('Failed to save spatial review:', err);
      setError('Failed to save projection settings. Please try again.');
      stopVerificationProgress();
      setUploadPhase('ready');
    } finally {
      setIsImporting(false);
    }
  };

  const resetModal = useCallback(() => {
    const nextStep = mode === 'table'
      ? initialStep === 'bigquery-dataset'
        ? 'connections'
        : 'upload'
      : 'select';
    setStep(nextStep);
    setActiveSourceTab(nextStep === 'connections' ? 'connections' : nextStep === 'samples' ? 'catalog' : 'upload');
    setSearchQuery('');
    setCatalogSampleFilters(createEmptyCatalogSampleFilters());
    setCatalogFiltersOpen(false);
    setSelection({ project: null, dataset: null, tables: [] });
    setDatasets([]);
    setTables([]);
    resetUploadState();
    setError(null);
    setCurrentPage(1);
    setCurrentTablePage(1);
    setExpandedLibraryNodes(new Set());
    libraryTreeExpandedOnceRef.current = false;
  }, [initialStep, mode, resetUploadState]);

  useEffect(() => {
    if (isOpen) {
      resetModal();
    }
  }, [isOpen, resetModal]);

  useEffect(() => {
    if (step !== 'samples') {
      setCatalogFiltersOpen(false);
    }
  }, [step]);

  useEffect(() => {
    return () => {
      stopVerificationProgress();
    };
  }, [stopVerificationProgress]);

  useEffect(() => {
    if (isOpen && mode === 'table' && step === 'samples' && sampleDatasets.length === 0) {
      loadSampleDatasets();
    }
  }, [isOpen, mode, step, sampleDatasets.length, loadSampleDatasets]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
    setCurrentTablePage(1);
  }, [searchQuery]);

  // Reset pagination when datasets are loaded
  useEffect(() => {
    setCurrentPage(1);
  }, [datasets.length]);

  // Reset table pagination when tables are loaded
  useEffect(() => {
    setCurrentTablePage(1);
  }, [tables.length]);

  const filteredDatasets = datasets.filter(d =>
    d.dataset_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination for datasets
  const totalPages = Math.ceil(filteredDatasets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDatasets = filteredDatasets.slice(startIndex, endIndex);

  const filteredLibrary = allUserDatasets
    .filter(d => {
      // Enforce single source type per workspace: BigQuery OR samples OR uploads
      const hasBigQuery = currentSources.some(s => s.type === 'bigquery');
      const hasSamples = currentSources.some(s => s.type === 'sample');
      const hasUploads = currentSources.some(s => isUploadDatasetType(s.type));

      // If workspace has BigQuery, only show BigQuery datasets
      if (hasBigQuery) return d.type === 'bigquery';

      // If workspace has samples, only show samples
      if (hasSamples) return d.type === 'sample';

      // If workspace has uploads, only show uploads
      if (hasUploads) return isUploadDatasetType(d.type);

      // Empty workspace: show all
      return true;
    })
    .filter(d => (d.name || d.id || "").toLowerCase().includes(searchQuery.toLowerCase()));

  const filteredLibraryTree = useMemo(
    () => buildSortedDatasetTree(filteredLibrary),
    [filteredLibrary]
  );

  const hasLibraryDatasets =
    hasManteoDatasets(filteredLibraryTree.manteo) ||
    Object.keys(filteredLibraryTree.bigquery).length > 0;

  useEffect(() => {
    if (!isOpen || step !== 'existing' || libraryTreeExpandedOnceRef.current || isLoading) return;
    const nodeIds = collectDatasetTreeNodeIds(filteredLibraryTree);
    if (nodeIds.size === 0) return;
    setExpandedLibraryNodes(nodeIds);
    libraryTreeExpandedOnceRef.current = true;
  }, [isOpen, step, filteredLibraryTree, isLoading]);

  const toggleLibraryNode = (nodeId) => {
    setExpandedLibraryNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const handleLibraryDatasetSelect = (dataset) => {
    if (mode === 'workspace' && currentWorkspaceDatasetCount >= MAX_DATASETS_PER_WORKSPACE) {
      setError(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
      return;
    }
    if (dataset.status && dataset.status !== 'ready') return;
    onConnect('library', dataset);
    onClose();
    resetModal();
  };

  const filteredTables = tables.filter(t =>
    (t.table_id || t.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination for tables
  const totalTablePages = Math.ceil(filteredTables.length / itemsPerPage);
  const tableStartIndex = (currentTablePage - 1) * itemsPerPage;
  const tableEndIndex = tableStartIndex + itemsPerPage;
  const paginatedTables = filteredTables.slice(tableStartIndex, tableEndIndex);

  if (!isOpen) return null;

  const isWideDataCatalog = step === 'samples';

  const connectionOptions = [
    {
      id: 'bigquery',
      title: 'Google BigQuery',
      desc: bigqueryEnabled ? 'Import tables from your cloud warehouse' : 'Upgrade required to enable BigQuery',
      iconSrc: '/icons/conn/conn-bigquery.svg',
      active: true,
      disabled: !bigqueryEnabled,
      status: bigqueryEnabled ? null : 'Pro feature'
    },
    {
      id: 'snowflake',
      title: 'Snowflake',
      desc: 'Cloud data warehouse',
      iconSrc: '/icons/conn/conn-snowflake.png',
      active: false,
      disabled: true,
      status: 'Coming soon'
    },
    {
      id: 'postgresql',
      title: 'PostgreSQL',
      desc: 'Open source database',
      iconSrc: '/icons/conn/conn-postgres.png',
      active: false,
      disabled: true,
      status: 'Coming soon'
    },
    {
      id: 'stac',
      title: 'STAC Catalog',
      desc: 'SpatioTemporal Asset Catalogs',
      iconSrc: '/icons/conn/conn-stac.png',
      active: false,
      disabled: true,
      status: 'Coming soon'
    },
    {
      id: 'more',
      title: 'More sources',
      desc: 'Additional connectors are coming soon',
      active: false,
      disabled: true,
      status: 'Coming soon'
    }
  ];

  const sourceTabs = [
    { id: 'upload', label: 'Upload', icon: Upload, step: 'upload' },
    { id: 'connections', label: 'Connections', icon: Database, step: 'connections' },
    { id: 'catalog', label: 'Catalog', icon: Library, step: 'samples' }
  ];

  const uploadFormatOptions = [
    {
      id: 'parquet',
      title: 'Parquet',
      desc: 'Columnar',
      iconSrc: '/icons/data/data-parquet.png',
      active: true
    },
    {
      id: 'csv',
      title: 'CSV / TSV',
      desc: 'Delimited',
      iconSrc: '/icons/data/data-csv.svg',
      active: true
    },
    {
      id: 'geojson',
      title: 'GeoJSON',
      desc: 'Spatial JSON',
      iconSrc: '/icons/data/data-geojson.svg',
      active: true
    },
    {
      id: 'shp',
      title: 'Shapefile',
      desc: 'Zipped (.zip)',
      iconSrc: '/icons/data/data-shp.png',
      active: true
    },
    {
      id: 'gpkg',
      title: 'GeoPackage',
      desc: 'GPKG',
      iconSrc: '/icons/data/data-geojson.svg',
      active: true
    },
    {
      id: 'kml',
      title: 'KML',
      desc: 'Keyhole',
      iconSrc: '/icons/data/data-geojson.svg',
      active: true
    },
    {
      id: 'cog',
      title: 'COG',
      desc: 'Raster',
      iconSrc: '/icons/data/data-cog.png',
      active: false,
      status: 'Coming soon'
    },
    {
      id: 'zarr',
      title: 'Zarr',
      desc: 'Arrays',
      iconSrc: '/icons/data/data-zarr.webp',
      active: false,
      status: 'Coming soon'
    }
  ];

  const handleSourceTabChange = (tab) => {
    if (isUploading || isImporting) return;
    setActiveSourceTab(tab.id);
    setStep(tab.step);
    setSearchQuery('');
    setError(null);
    if (tab.id !== 'upload') {
      resetUploadState();
    }
    if (tab.id === 'catalog') {
      loadSampleDatasets();
    }
  };

  const options = mode === 'connection' ? connectionOptions : (() => {
    // For table mode (DataSourcesPage), only BigQuery is supported
    if (mode === 'table') {
      return [
        {
          id: 'bigquery',
          title: 'Google BigQuery',
          desc: bigqueryEnabled ? 'Import from cloud data warehouse' : 'Upgrade required to enable BigQuery',
          icon: Database,
          color: 'text-blue-600',
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          disabled: !bigqueryEnabled
        },
        {
          id: 'upload',
          title: 'Manteo cloud',
          desc: 'Upload files or select samples',
          icon: Upload,
          color: 'text-emerald-600',
          bg: 'bg-emerald-50 dark:bg-emerald-900/20',
          disabled: false
        }
      ];
    }

    // For workspace mode, enforce single source type: BigQuery OR samples OR uploads (not mixed)
    const hasBigQuery = currentSources.some(s => s.type === 'bigquery');
    const hasSamples = currentSources.some(s => s.type === 'sample');
    const hasUploads = currentSources.some(s => isUploadDatasetType(s.type));

    // Determine which type is locked in (if any)
    const lockedType = hasBigQuery ? 'bigquery' : hasSamples ? 'sample' : hasUploads ? 'upload' : null;

    return [
      {
        id: 'bigquery',
        title: 'Google BigQuery',
        desc: !bigqueryEnabled
          ? 'Upgrade required to enable BigQuery'
          : lockedType && lockedType !== 'bigquery'
          ? `Disabled: Workspace uses ${lockedType === 'sample' ? 'samples' : 'uploads'}`
          : 'Import from cloud data warehouse',
        icon: Database,
        color: 'text-blue-600',
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        disabled: !bigqueryEnabled || lockedType === 'sample' || lockedType === 'upload'
      },
      {
        id: 'existing',
        title: 'Manteo cloud',
        desc: lockedType && lockedType !== 'bigquery'
          ? `Disabled: Workspace uses ${lockedType === 'sample' ? 'samples' : 'uploads'}`
          : 'Attach an existing dataset',
        icon: Library,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        disabled: lockedType === 'sample' || lockedType === 'upload'
      },
      {
        id: 'upload',
        title: 'Manteo cloud',
        desc: lockedType && lockedType !== 'bigquery'
          ? `Disabled: Workspace uses ${lockedType === 'sample' ? 'samples' : 'uploads'}`
          : 'Upload files or select samples',
        icon: Upload,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        disabled: lockedType === 'sample' || lockedType === 'bigquery'
      }
    ];
  })();

  const renderConnectionGrid = () => (
    <div className="grid grid-cols-2 gap-3">
      {connectionOptions.map((opt) => {
        const isBigQuery = opt.id === 'bigquery';
        const isLocked = isBigQuery && !bigqueryEnabled;
        const isComingSoon = !opt.active;
        const StatusIcon = isLocked ? Lock : isComingSoon ? Clock3 : Check;

        const isConnected = isBigQuery && !!bqStatus?.connected;

        const handleActivate = () => {
          if (opt.disabled) return;
          if (isBigQuery) handleBQLanding();
        };

        return (
          <div
            key={opt.id}
            role="button"
            tabIndex={opt.disabled ? -1 : 0}
            aria-disabled={opt.disabled}
            onClick={handleActivate}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleActivate();
              }
            }}
            className={cn(
              "group relative min-h-[170px] rounded-[28px] border p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
              opt.disabled
                ? "cursor-not-allowed border-gray-100 bg-gray-50/70 opacity-80 dark:border-gray-800 dark:bg-gray-900/50"
                : "border-blue-100 bg-white shadow-sm hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md dark:border-blue-900/40 dark:bg-gray-900 dark:hover:border-blue-700"
            )}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white p-2 shadow-sm ring-1 ring-gray-100 dark:bg-gray-950 dark:ring-gray-800">
                {opt.iconSrc ? (
                  <img src={opt.iconSrc} alt="" className={cn("h-10 w-10 object-contain", isComingSoon && "grayscale")} />
                ) : (
                  <Database size={28} className="text-gray-400 dark:text-gray-500" />
                )}
              </div>
              {opt.status && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-1 text-[8px] font-black uppercase tracking-widest",
                    isLocked
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                      : isComingSoon
                      ? "bg-gray-200 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                      : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  )}
                >
                  <StatusIcon size={10} strokeWidth={3} />
                  {opt.status}
                </span>
              )}
            </div>
            <h4 className="text-sm font-black uppercase tracking-tight text-gray-900 dark:text-white">{opt.title}</h4>
            <p className="mt-1.5 text-[10px] font-semibold leading-relaxed text-gray-500 dark:text-gray-400">{opt.desc}</p>
            {isBigQuery && bqStatus?.reauth_required && (
              <div className="mt-3 inline-flex items-center gap-1 rounded-lg bg-red-100/70 px-2 py-1 text-[8px] font-black uppercase tracking-widest text-red-600 dark:bg-red-900/30 dark:text-red-400">
                <AlertCircle size={10} />
                Re-auth required
              </div>
            )}
            {isBigQuery && bqStatus?.connected && !bqStatus.reauth_required && !bqStatus.billing_project_id && (
              <div className="mt-3 inline-flex items-center gap-1 rounded-lg bg-amber-100/70 px-2 py-1 text-[8px] font-black uppercase tracking-widest text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                <AlertCircle size={10} />
                Setup required
              </div>
            )}
            {isConnected && (
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onBillingRequired) {
                      onBillingRequired();
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  title="Connection settings"
                >
                  <Settings size={10} />
                  Settings
                </button>
                <button
                  onClick={handleDisconnectBigQuery}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Unlink connection"
                >
                  <Unplug size={10} />
                  Unlink
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderUploadFormatGrid = () => {
    const availableFormats = uploadFormatOptions.filter(format => format.active);
    const comingSoonFormats = uploadFormatOptions.filter(format => !format.active);

    return (
      <div className="rounded-[28px] border border-gray-100 bg-gray-50/70 p-4 dark:border-gray-800 dark:bg-gray-900/50">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-900 dark:text-white">Available Formats</h4>
            <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              More formats are coming soon
            </p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            <Clock3 size={9} strokeWidth={3} />
            More soon
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {availableFormats.map(format => (
            <div
              key={format.id}
              className="flex items-center gap-2 rounded-2xl bg-white px-2.5 py-2 shadow-sm ring-1 ring-gray-100 dark:bg-gray-950 dark:ring-gray-800"
            >
              <img src={format.iconSrc} alt="" className="h-7 w-7 object-contain" />
              <span className="text-[9px] font-black uppercase tracking-widest text-gray-800 dark:text-gray-100">{format.title}</span>
            </div>
          ))}

          <div className="flex items-center gap-1.5 rounded-2xl border border-dashed border-gray-200 px-2.5 py-2 dark:border-gray-700">
            {comingSoonFormats.map(format => (
              <img
                key={format.id}
                src={format.iconSrc}
                alt=""
                title={`${format.title} coming soon`}
                className="h-7 w-7 object-contain opacity-45 grayscale"
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const needsLayerReview = uploadPhase === 'ready' && uploadGate === 'layer';
  const needsProjectionReview = uploadPhase === 'ready' && uploadGate === 'crs';
  const needsUploadReview = needsLayerReview || needsProjectionReview;
  const isUploadBusy = isUploading || isImporting;
  const isUploadLocked = isUploadBusy || needsUploadReview;
  const uploadStatusTitle = {
    idle: isDragging ? 'Drop to Upload' : 'Upload Your Own File',
    preparing: 'Preparing Upload...',
    uploading: 'Uploading File...',
    verifying: 'Verifying Dataset...',
    ready: needsLayerReview
      ? 'Select layer'
      : needsProjectionReview
        ? 'Confirm projection'
        : 'Upload Complete',
  }[uploadPhase];
  const uploadStatusDescription = {
    idle: 'Drop files or click to browse',
    preparing: 'Requesting secure storage access',
    uploading: `${uploadProgress}% uploaded${uploadFileName ? `: ${uploadFileName}` : ''}`,
    verifying: 'Checking the dataset and metadata',
    ready: needsLayerReview
      ? 'This file contains multiple layers. Choose one to import.'
      : needsProjectionReview
        ? 'Pick the coordinate reference system for this dataset.'
        : 'Ready',
  }[uploadPhase];
  const uploadProgressPercent = {
    idle: 0,
    preparing: 8,
    uploading: Math.max(10, Math.round(uploadProgress * 0.8)),
    verifying: uploadProgress,
    ready: 100
  }[uploadPhase];
  const showUploadProgress = uploadPhase !== 'idle' && uploadPhase !== 'ready';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity duration-500" onClick={onClose} />
      <div
        className={cn(
          'relative bg-white dark:bg-gray-950 w-full rounded-[48px] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.3)] border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-8 duration-500 flex flex-col max-h-[92vh]',
          isWideDataCatalog
            ? 'max-w-[min(1120px,calc(100vw-1.5rem))] sm:max-w-2xl md:max-w-4xl lg:max-w-5xl'
            : 'max-w-lg md:max-w-xl'
        )}
      >

        {/* Header */}
        <div className="p-8 border-b border-gray-50 dark:border-gray-900/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            {step !== 'select' && !(mode === 'table' && ['upload', 'connections', 'samples'].includes(step)) && (
              <button onClick={handleBack} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-xl transition-colors">
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter leading-none">
               {mode === 'table' && ['upload', 'connections', 'samples'].includes(step) && "Add Dataset"}
               {step === 'select' && (
                  mode === 'connection'
                    ? (bqStatus?.connected ? "Manage Connection" : "Add Connection")
                    : "Add Dataset"
                )}
                {!(mode === 'table' && ['upload', 'connections', 'samples'].includes(step)) && step === 'upload' && "Manteo cloud"}
                {!(mode === 'table' && ['upload', 'connections', 'samples'].includes(step)) && step === 'connections' && "Connections"}
                {!(mode === 'table' && ['upload', 'connections', 'samples'].includes(step)) && step === 'samples' && "Sample Datasets"}
                {step === 'existing' && "Select from Repository"}
                {step === 'bigquery-auth' && "Connect BigQuery"}
                {step === 'bigquery-dataset' && "Select Dataset"}
                {step === 'bigquery-table' && "Select Tables"}
                {step === 'bigquery-confirm' && "Review Import"}
              </h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                {mode === 'table' && ['upload', 'connections', 'samples'].includes(step) && "Choose a source type"}
                {step === 'select' && (mode === 'connection' ? "Configure your data source provider" : "Choose how you want to add data")}
                {!(mode === 'table' && ['upload', 'connections', 'samples'].includes(step)) && step === 'upload' && "Upload your own files or select samples"}
                {!(mode === 'table' && ['upload', 'connections', 'samples'].includes(step)) && step === 'connections' && "Import from connected providers"}
                {!(mode === 'table' && ['upload', 'connections', 'samples'].includes(step)) && step === 'samples' && "Choose from available sample datasets"}
                {step === 'existing' && "Select a dataset already in Manteo cloud"}
                {step === 'bigquery-auth' && "Authenticate via Google SSO"}
                {step === 'bigquery-dataset' && "Choose a BigQuery dataset"}
                {step === 'bigquery-table' && (selection.project ? `${selection.project.friendly_name} / ${selection.dataset?.dataset_id}` : "Select tables to import")}
                {step === 'bigquery-confirm' && "Verify selected datasets before adding"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-8 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 shrink-0">
            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest">Error</p>
              <p className="text-xs font-bold text-red-500 dark:text-red-300 mt-1">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg transition-colors">
              <X size={14} className="text-red-400" />
            </button>
          </div>
        )}

        {mode === 'table' && ['upload', 'connections', 'samples', 'bigquery-auth', 'bigquery-dataset', 'bigquery-table', 'bigquery-confirm'].includes(step) && (
          <div className="px-6 pt-5 shrink-0">
            <div className="grid grid-cols-3 gap-1 rounded-2xl bg-gray-100 p-1 dark:bg-gray-900">
              {sourceTabs.map((tab) => {
                const TabIcon = tab.icon;
                const isActive = activeSourceTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleSourceTabChange(tab)}
                    disabled={isUploadBusy}
                    className={cn(
                      "flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all",
                      isActive
                        ? "bg-white text-emerald-700 shadow-sm dark:bg-gray-800 dark:text-emerald-300"
                        : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white",
                      isUploadBusy && "cursor-not-allowed opacity-60"
                    )}
                  >
                    <TabIcon size={13} strokeWidth={2.5} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div
          className={cn(
            'flex-1 overflow-y-auto custom-scrollbar min-h-[300px]',
            isWideDataCatalog ? 'p-5 sm:p-8 min-h-[360px]' : 'p-6'
          )}
        >
          {step === 'select' && (
            mode === 'connection' ? renderConnectionGrid() : (
              <div className="space-y-2">
                {options.map((opt) => (
                  <button
                    key={opt.id}
                    disabled={opt.disabled}
                    onClick={() => {
                      if (opt.id === 'bigquery') handleBQLanding();
                      else setStep(opt.id);
                    }}
                    className={cn(
                      "w-full flex items-center gap-4 p-5 rounded-3xl transition-all border border-transparent text-left",
                      opt.disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-100 dark:hover:border-gray-800 group"
                    )}
                  >
                    <div className={cn("p-4 rounded-2xl transition-transform shadow-sm", !opt.disabled && "group-hover:scale-110", opt.bg, opt.color)}>
                      <opt.icon size={28} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">{opt.title}</span>
                        {opt.id === 'bigquery' && bqStatus?.reauth_required && (
                          <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100/50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
                            <AlertCircle size={10} />
                            <span className="text-[8px] font-black uppercase tracking-widest">Re-auth Required</span>
                          </div>
                        )}
                        {opt.id === 'bigquery' && bqStatus?.connected && !bqStatus.reauth_required && !bqStatus.billing_project_id && (
                          <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100/50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                            <AlertCircle size={10} />
                            <span className="text-[8px] font-black uppercase tracking-widest">Setup Required</span>
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-500 font-medium mt-0.5">{opt.desc}</p>
                    </div>
                    {!opt.disabled && <ChevronRight size={20} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />}
                  </button>
                ))}
              </div>
            )
          )}

          {step === 'connections' && (
            renderConnectionGrid()
          )}

          {step === 'upload' && (
            <div className="space-y-3">
              {mode !== 'table' && (
                <>
                  <button
                    onClick={() => {
                      loadSampleDatasets();
                      setStep('samples');
                    }}
                    className="w-full flex items-center gap-4 p-5 rounded-3xl transition-all border border-transparent hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-100 dark:hover:border-gray-800 group"
                  >
                    <div className="p-4 rounded-2xl transition-transform shadow-sm group-hover:scale-110 bg-emerald-50 dark:bg-emerald-900/25 text-emerald-600 dark:text-emerald-400">
                      <Library size={28} />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Select Sample Dataset</span>
                      <p className="text-[10px] text-gray-500 font-medium mt-0.5">Choose from pre-loaded examples</p>
                    </div>
                    <ChevronRight size={20} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
                  </button>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
                    </div>
                    <div className="relative flex justify-center text-[8px] uppercase tracking-widest">
                      <span className="px-2 bg-white dark:bg-gray-950 text-gray-400 font-black">Or</span>
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept={ALLOWED_DATASET_UPLOAD_ACCEPT}
                />
                <div
                  className={cn(
                    "w-full border-2 border-dashed rounded-[40px] p-12 flex flex-col items-center justify-center group transition-all",
                    isUploadLocked ? "opacity-80 cursor-default border-gray-200 dark:border-gray-800" :
                      isDragging ? "border-emerald-500 bg-emerald-50/10 dark:bg-emerald-900/10 scale-[1.02]" :
                        "border-gray-200 dark:border-gray-800 hover:border-emerald-500/50 hover:bg-emerald-50/5 dark:hover:bg-emerald-900/5 cursor-pointer"
                  )}
                  onClick={() => !isUploadLocked && handleUploadClick()}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className={cn(
                    "p-6 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-[32px] mb-6 transition-transform",
                    isDragging ? "scale-110" : "group-hover:scale-110"
                  )}>
                    {isUploadBusy && !needsProjectionReview ? (
                      <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Upload size={32} />
                    )}
                  </div>
                  <h4 className="mb-2 max-w-full px-3 text-center text-sm font-semibold leading-snug text-gray-900 dark:text-gray-100">
                    {uploadStatusTitle}
                  </h4>
                  <p className="max-w-full px-3 text-center text-[11px] font-normal leading-snug text-gray-500 dark:text-gray-400 break-words">
                    {uploadStatusDescription}
                  </p>
                  {uploadPhase === 'idle' && (
                    <p className="mt-2 text-center text-[10px] text-gray-400 dark:text-gray-500">
                      Max file size {formatDatasetUploadMaxSizeLabel()}.
                    </p>
                  )}
                  {showUploadProgress && (
                    <div className="mt-6 w-full max-w-xs">
                      <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-900">
                        <div
                          className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                          style={{ width: `${uploadProgressPercent}%` }}
                        />
                      </div>
                      <div className="mt-3 flex items-center justify-between text-[10px] font-medium tabular-nums text-gray-500 dark:text-gray-400">
                        <span>{uploadPhase}</span>
                        <span>{uploadProgressPercent}%</span>
                      </div>
                    </div>
                  )}
                </div>
                {needsLayerReview && (
                  <div className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500">
                      Layer
                    </label>
                    <select
                      value={selectedLayer}
                      onChange={(e) => setSelectedLayer(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-900 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
                    >
                      {layerOptions.map((layer) => (
                        <option key={layer.name} value={layer.name}>
                          {layer.title || layer.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleContinueUploadReview}
                      disabled={isImporting || !selectedLayer}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
                    >
                      {isImporting ? (
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin dark:border-gray-900/30 dark:border-t-gray-900" />
                      ) : null}
                      {isImporting ? 'Importing…' : 'Continue'}
                    </button>
                  </div>
                )}
                {needsProjectionReview && (
                  <div className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
                    <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                      Choose the coordinate reference system for this dataset so geometry displays correctly on the map.
                    </p>
                    <SpatialSelector
                      columnName="Dataset"
                      value={spatialReview.dataset}
                      onChange={(val) => setSpatialReview({ dataset: val })}
                    />
                    <button
                      type="button"
                      onClick={handleContinueUploadReview}
                      disabled={isImporting}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
                    >
                      {isImporting ? (
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin dark:border-gray-900/30 dark:border-t-gray-900" />
                      ) : null}
                      {isImporting ? 'Importing…' : 'Continue'}
                    </button>
                  </div>
                )}
                {!needsUploadReview && renderUploadFormatGrid()}
              </div>
            </div>
          )}

          {step === 'samples' && (
            <div className="flex flex-col gap-4 min-h-[300px]">
              {catalogFiltersOpen ? (
                <button
                  type="button"
                  aria-label="Close filters"
                  className="fixed inset-0 z-[105] bg-slate-900/40 backdrop-blur-[2px] lg:hidden"
                  onClick={() => setCatalogFiltersOpen(false)}
                />
              ) : null}
              <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 min-h-0 flex-1 lg:items-stretch">
                <aside
                  id="catalog-sample-filters"
                  className={cn(
                    'shrink-0 overflow-y-auto rounded-3xl border border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white p-4 shadow-sm ring-1 ring-slate-900/5 dark:border-gray-800 dark:from-gray-900/90 dark:to-gray-950 dark:ring-white/10',
                    catalogFiltersOpen
                      ? 'fixed inset-y-0 left-0 z-[106] block w-[min(88vw,320px)] max-h-none rounded-none rounded-r-3xl shadow-2xl'
                      : 'hidden',
                    'lg:static lg:block lg:w-64 xl:w-72 lg:max-h-[min(62vh,520px)] lg:rounded-3xl lg:shadow-sm'
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-2.5 pb-2.5 border-b border-slate-200/80 dark:border-gray-800">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <SlidersHorizontal size={14} className="shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                      <span className="text-sm font-medium text-slate-700 dark:text-gray-200 truncate">
                        Filters
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {activeCatalogFilterCount > 0 ? (
                        <button
                          type="button"
                          onClick={clearAllCatalogSampleFilters}
                          className="text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                        >
                          Clear all
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setCatalogFiltersOpen(false)}
                        className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white lg:hidden"
                        aria-label="Close filters"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  {!Object.values(catalogFacetOptions).some((a) => a.length > 0) && !isLoading ? (
                    <p className="text-[10px] text-slate-500 dark:text-gray-400 leading-relaxed">
                      No facet metadata on loaded samples yet.
                    </p>
                  ) : null}
                  <CatalogFacetCheckboxGroup
                    label="Category"
                    options={catalogFacetOptions.categories}
                    selected={catalogSampleFilters.categories}
                    onToggle={(v) => toggleCatalogFacet('categories', v)}
                    onClearSection={() => clearCatalogFacet('categories')}
                  />
                  <CatalogFacetCheckboxGroup
                    label="Type"
                    options={catalogFacetOptions.types}
                    selected={catalogSampleFilters.types}
                    onToggle={(v) => toggleCatalogFacet('types', v)}
                    onClearSection={() => clearCatalogFacet('types')}
                  />
                  <CatalogFacetCheckboxGroup
                    label="Tags"
                    options={catalogFacetOptions.tags}
                    selected={catalogSampleFilters.tags}
                    onToggle={(v) => toggleCatalogFacet('tags', v)}
                    onClearSection={() => clearCatalogFacet('tags')}
                  />
                  <CatalogFacetCheckboxGroup
                    label="License"
                    options={catalogFacetOptions.licenses}
                    selected={catalogSampleFilters.licenses}
                    onToggle={(v) => toggleCatalogFacet('licenses', v)}
                    onClearSection={() => clearCatalogFacet('licenses')}
                  />
                  <CatalogFacetCheckboxGroup
                    label="Source"
                    options={catalogFacetOptions.dataSources}
                    selected={catalogSampleFilters.dataSources}
                    onToggle={(v) => toggleCatalogFacet('dataSources', v)}
                    onClearSection={() => clearCatalogFacet('dataSources')}
                  />
                  <CatalogFacetCheckboxGroup
                    label="Spatial"
                    options={catalogFacetOptions.spatial}
                    selected={catalogSampleFilters.spatial}
                    onToggle={(v) => toggleCatalogFacet('spatial', v)}
                    onClearSection={() => clearCatalogFacet('spatial')}
                  />
                  <CatalogFacetCheckboxGroup
                    label="Temporal"
                    options={catalogFacetOptions.temporal}
                    selected={catalogSampleFilters.temporal}
                    onToggle={(v) => toggleCatalogFacet('temporal', v)}
                    onClearSection={() => clearCatalogFacet('temporal')}
                  />
                </aside>
                <div className="flex-1 min-w-0 flex flex-col gap-4 min-h-0">
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCatalogFiltersOpen(true)}
                      className="flex h-[50px] shrink-0 items-center gap-2 rounded-2xl border border-slate-200/80 bg-slate-50 px-3.5 text-xs font-medium text-slate-600 transition-colors hover:border-emerald-300/70 hover:bg-white dark:border-gray-800 dark:bg-gray-900/80 dark:text-gray-300 dark:hover:border-emerald-700/45 lg:hidden"
                      aria-expanded={catalogFiltersOpen}
                      aria-controls="catalog-sample-filters"
                    >
                      <Menu size={18} className="text-emerald-600 dark:text-emerald-400" aria-hidden />
                      Filters
                      {activeCatalogFilterCount > 0 ? (
                        <span className="rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold tabular-nums text-white dark:bg-emerald-500">
                          {activeCatalogFilterCount}
                        </span>
                      ) : null}
                    </button>
                    <div className="relative group min-w-0 flex-1">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                      <input
                        type="text"
                        placeholder="Search name, description, coverage, tags…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-gray-900/80 border border-slate-200/80 dark:border-gray-800 focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/15 rounded-2xl text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 outline-none transition-all"
                      />
                    </div>
                  </div>
                  <CatalogSampleLicenseDisclaimer className="shrink-0" />
                  <div className="space-y-2 pt-0 overflow-y-auto max-h-[min(58vh,480px)] lg:max-h-[min(68vh,600px)] pr-0.5">
                    {isLoading ? (
                      <div className="py-16 flex flex-col items-center justify-center gap-4">
                        <div className="w-9 h-9 border-[3px] border-emerald-500/15 border-t-emerald-600 rounded-full animate-spin dark:border-emerald-400/20 dark:border-t-emerald-400" />
                        <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">
                          Loading catalog…
                        </span>
                      </div>
                    ) : filteredCatalogSamples.length === 0 ? (
                      <div className="py-16 text-center text-slate-500 dark:text-gray-400 text-[11px] font-semibold">
                        No datasets match your filters
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
                        {filteredCatalogSamples.map((sample) => (
                          <button
                            key={sample.id}
                            type="button"
                            onClick={async () => {
                              if (mode === 'workspace' && currentWorkspaceDatasetCount >= MAX_DATASETS_PER_WORKSPACE) {
                                setError(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
                                return;
                              }
                              setError(null);
                              setIsImporting(true);
                              try {
                                await datasetApi.addSample(sample.id);
                                onConnect('sample', sample);
                                onClose();
                                resetModal();
                              } catch (err) {
                                console.error('Failed to add sample:', err);
                                const detail = err.response?.data?.detail;
                                const message =
                                  (typeof detail === 'string' && detail) ||
                                  (Array.isArray(detail)
                                    ? detail.map((e) => e?.msg || JSON.stringify(e)).join(' ')
                                    : null) ||
                                  err.message ||
                                  'Failed to add sample dataset. Please try again.';
                                setError(message);
                              } finally {
                                setIsImporting(false);
                              }
                            }}
                            disabled={isImporting}
                            className={cn(
                              'group flex flex-col overflow-hidden rounded-2xl border text-left transition-all duration-200',
                              isImporting
                                ? 'cursor-not-allowed border-slate-200/60 opacity-60 dark:border-gray-800'
                                : 'border-slate-200/80 bg-white shadow-md hover:border-emerald-300/70 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900/90 dark:hover:border-emerald-700/45'
                            )}
                          >
                            <div className="relative aspect-[5/3] w-full shrink-0 overflow-hidden bg-slate-100 dark:bg-gray-800">
                              <div className="absolute inset-x-0 top-0 z-10 flex flex-wrap items-center gap-1.5 p-2">
                                {sample.category ? (
                                  <span className="max-w-[65%] truncate rounded-md bg-black/50 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-white backdrop-blur-sm dark:bg-black/55">
                                    {sample.category}
                                  </span>
                                ) : null}
                                {(sample.types || []).map((t) => (
                                  <span
                                    key={t}
                                    className="rounded-md border border-white/50 bg-white/90 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-slate-700 shadow-sm backdrop-blur-sm dark:border-white/25 dark:bg-white/25 dark:text-white"
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                              <CatalogSampleThumbnail url={sample.thumbnail_url} seed={sample.id} />
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col gap-1.5 p-3.5 sm:p-4">
                              <div className="flex items-start justify-between gap-2">
                                <span className="line-clamp-2 min-w-0 flex-1 text-left text-sm font-bold leading-snug tracking-tight text-slate-900 dark:text-white">
                                  {sample.name}
                                </span>
                                <ChevronRight
                                  size={18}
                                  className="mt-0.5 shrink-0 text-slate-300 transition-colors group-hover:text-emerald-600 dark:text-gray-600 dark:group-hover:text-emerald-400"
                                />
                              </div>
                              {sample.description ? (
                                <p className="line-clamp-3 text-left text-xs leading-relaxed text-slate-600 dark:text-gray-400">
                                  {sample.description}
                                </p>
                              ) : null}
                              <CatalogDatasetMeta
                                url={sample.url}
                                license={sample.license}
                                stopPropagation
                              />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}


          {step === 'existing' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Search your library..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              <div className="rounded-lg border border-gray-100 dark:border-gray-800/80 divide-y divide-gray-100 dark:divide-gray-800/80 pt-1">
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Library...</span>
                  </div>
                ) : !hasLibraryDatasets ? (
                  <div className="py-12 text-center text-[10px] font-black uppercase tracking-widest text-gray-400">No datasets found</div>
                ) : (
                  <DatasetInventoryTree
                    tree={filteredLibraryTree}
                    userFolderLabel={userFolderLabel}
                    expandedNodes={expandedLibraryNodes}
                    onToggleNode={toggleLibraryNode}
                    renderDatasetLeaf={(item, level) => {
                      const isBigQuery = item.type === 'bigquery';
                      const isDisabled = item.status && item.status !== 'ready';
                      return (
                        <InventoryTreeNode
                          key={item.id}
                          item={datasetLeafItem(item)}
                          level={level}
                          onSelect={() => handleLibraryDatasetSelect(item)}
                          icon={datasetLeafIcon}
                          iconColor={isBigQuery ? 'text-blue-500/60' : 'text-emerald-500/70'}
                          isDisabled={isDisabled}
                        />
                      );
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {step === 'bigquery-auth' && (
            <div className="h-full flex flex-col items-center justify-center p-8 space-y-8">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full" />
                <div className="relative p-8 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-[40px] shadow-sm">
                  <ShieldCheck size={48} />
                </div>
              </div>
              <div className="text-center">
                <h4 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight mb-2">Google Cloud Authentication</h4>
                <p className="text-[11px] text-gray-500 font-medium max-w-[280px] mx-auto leading-relaxed">
                  We need your permission to access your BigQuery projects and datasets.
                </p>
              </div>
              <button
                onClick={handleBQAuth}
                className="w-full flex items-center justify-center gap-3 bg-blue-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20 active:scale-[0.98]"
              >
                Connect Google Account
                <ExternalLink size={16} />
              </button>
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em]">Secure OAuth2 Verification</p>
            </div>
          )}


          {step === 'bigquery-dataset' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Filter datasets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              {!isLoading && filteredDatasets.length > 0 && totalPages > 1 && (
                <div className="flex items-center justify-between px-2 py-2">
                  <div className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredDatasets.length)} of {filteredDatasets.length} datasets
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className={cn(
                        "p-1.5 rounded-lg transition-all",
                        currentPage === 1
                          ? "opacity-50 cursor-not-allowed text-gray-400"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400"
                      )}
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <span className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest px-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage >= totalPages}
                      className={cn(
                        "p-1.5 rounded-lg transition-all",
                        currentPage >= totalPages
                          ? "opacity-50 cursor-not-allowed text-gray-400"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400"
                      )}
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-1">
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading datasets...</span>
                  </div>
                ) : filteredDatasets.length === 0 ? (
                  <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No datasets found</div>
                ) : (
                  <>
                    {paginatedDatasets.map((dataset) => (
                      <button
                        key={dataset.dataset_id}
                        onClick={() => handleDatasetSelect(dataset)}
                        className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-blue-50 dark:hover:bg-blue-900/20 group transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white dark:bg-gray-800 rounded-xl text-blue-600 shadow-sm">
                            <Database size={16} />
                          </div>
                          <div className="text-left font-black text-xs text-gray-900 dark:text-white uppercase tracking-tight">
                            {dataset.dataset_id}
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {step === 'bigquery-table' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Search in this dataset..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              {!isLoading && filteredTables.length > 0 && totalTablePages > 1 && (
                <div className="flex items-center justify-between px-2 py-2">
                  <div className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    Showing {tableStartIndex + 1}-{Math.min(tableEndIndex, filteredTables.length)} of {filteredTables.length} tables
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentTablePage(prev => Math.max(1, prev - 1))}
                      disabled={currentTablePage === 1}
                      className={cn(
                        "p-1.5 rounded-lg transition-all",
                        currentTablePage === 1
                          ? "opacity-50 cursor-not-allowed text-gray-400"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400"
                      )}
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <span className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest px-2">
                      Page {currentTablePage} of {totalTablePages}
                    </span>
                    <button
                      onClick={() => setCurrentTablePage(prev => Math.min(totalTablePages, prev + 1))}
                      disabled={currentTablePage >= totalTablePages}
                      className={cn(
                        "p-1.5 rounded-lg transition-all",
                        currentTablePage >= totalTablePages
                          ? "opacity-50 cursor-not-allowed text-gray-400"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400"
                      )}
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
              <div>
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Scanning tables...</span>
                  </div>
                ) : filteredTables.length === 0 ? (
                  <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No tables found</div>
                ) : (
                  <>
                    <div className="border border-gray-100/50 dark:border-gray-800/50 rounded-2xl overflow-hidden">
                      {paginatedTables.map((table) => (
                        <button
                          key={table.full_id}
                          onClick={() => toggleTableSelection({ id: table.full_id, name: table.table_id, ...table })}
                          className={cn(
                            "w-full flex items-center justify-between py-2.5 px-6 group transition-all border-b border-gray-50/50 dark:border-gray-800/30 last:border-b-0",
                            selection.tables.find(t => t.id === table.full_id)
                              ? "bg-blue-50/20 dark:bg-blue-900/10"
                              : "hover:bg-emerald-50/20 dark:hover:bg-emerald-900/5"
                          )}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Table size={11} className={cn(
                              selection.tables.find(t => t.id === table.full_id) ? "text-blue-600 dark:text-blue-400" : "text-gray-400"
                            )} />
                            <div className="flex-1 min-w-0 text-left">
                              <div className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider block truncate max-w-[300px]">
                                {table.table_id}
                              </div>
                              <div className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block max-w-[300px] mt-0.5 opacity-60">
                                {table.type}
                              </div>
                            </div>
                          </div>
                          {selection.tables.find(t => t.id === table.full_id) && (
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white shrink-0">
                              <Check size={12} strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {step === 'bigquery-confirm' && (
            <div className="space-y-2">
              <div className="mb-4 px-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Selected Tables</p>
              </div>
              {selection.tables.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">
                  No tables selected
                </div>
              ) : (
                selection.tables.map((table) => (
                  <div
                    key={table.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Table size={11} className="text-gray-400" />
                      <div className="flex-1 min-w-0 text-left">
                        <div className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider block truncate max-w-[300px]">
                          {table.table_id || table.name}
                        </div>
                        <div className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block max-w-[300px] mt-0.5 opacity-60">
                          {table.contextProject} • {table.contextDataset}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleTableSelection(table)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {selection.tables.length > 0 && (step === 'bigquery-table' || step === 'bigquery-confirm') && (
          <div className="p-8 border-t border-gray-50 dark:border-gray-900/50 bg-gray-50/30 dark:bg-gray-900/30 shrink-0">
            {step === 'bigquery-table' ? (
              <button
                onClick={() => setStep('bigquery-confirm')}
                className="w-full bg-emerald-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-2"
              >
                Add {selection.tables.length} {selection.tables.length === 1 ? 'Table' : 'Tables'}
                <Check size={16} strokeWidth={3} />
              </button>
            ) : (
              <button
                onClick={handleFinishBQ}
                disabled={isImporting}
                className={cn(
                  "w-full bg-emerald-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-2",
                  isImporting && "opacity-70 cursor-wait"
                )}
              >
                {isImporting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Generating Metadata...
                  </>
                ) : (
                  <>
                    Import Selected Tables
                    <Check size={16} strokeWidth={3} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectModal;
